import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Skeleton,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  styled,
  Modal,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from '@mui/material'
import { keyframes, styled as muiStyled } from '@mui/material/styles'
import {
  faMapMarkerAlt,
  faBuilding,
  faClock,
  faEuroSign,
  faHeart,
  faShare,
  faArrowLeft,
  faCalendarAlt,
  faUserTie,
  faGraduationCap,
  faBriefcase,
  faLanguage,
  faUsers,
  faBrain,
  faHandshake,
  faCar,
  faIdCard,
  faRoad,
  faChartLine,
  faUserCheck,
} from '@fortawesome/pro-solid-svg-icons'
import {
  faWhatsapp,
  faFacebook,
  faLinkedin,
  faTwitter,
} from '@fortawesome/free-brands-svg-icons'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, Link } from 'react-router-dom'

import moment from 'moment/moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons'
import ApplicationModal from '../../../../components/modals/ApplicationModal'
import lightTheme from '../../../../styles/theme/lightTheme'
import Api from '../../../../services/api.service'
import { handlingErrors } from '../../../../utils'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { ROLE_CANDIDAT, ROLE_ENTREPRISE } from '../../../../data'

// Pulse animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`

const LiveBadge = styled(Chip)(({ theme }) => ({
  height: 10,
  minWidth: 10,
  fontSize: '10px',
  fontWeight: 600,
  backgroundColor: '#4CAF50',
  color: '#FFFFFF',
  marginRight: 8,
  animation: `${pulse} 1.5s infinite`,
  '& .MuiChip-label': {
    padding: '0 4px',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '11px',
  },
}))

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  overflow: 'hidden',
  marginBottom: '24px',
  [theme.breakpoints.down('sm')]: {
    borderRadius: '14px',
  },
}))

const InfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  borderRadius: '12px',
  marginBottom: '16px',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
    padding: '14px',
  },
}))

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(99, 102, 241, 0.08)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  color: '#6366f1',
  fontWeight: 600,
  fontSize: '0.875rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  '&:hover': {
    backgroundColor: 'rgba(99, 102, 241, 0.12)',
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
    margin: '3px',
  },
}))

const HTMLContent = muiStyled('div')(({ theme }) => ({
  '& p': {
    marginBottom: '16px',
    lineHeight: 1.6,
    color: '#4b5563',
    fontSize: '1rem',
  },
  '& strong': {
    fontWeight: 600,
    color: '#1a1a1a',
  },
  '& ul': {
    paddingLeft: '20px',
    marginBottom: '16px',
  },
  '& li': {
    marginBottom: '8px',
    lineHeight: 1.5,
    color: '#4b5563',
  },
}))

const LanguageChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(16, 185, 129, 0.08)',
  border: '1px solid rgba(16, 185, 129, 0.2)',
  color: '#10b981',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(16, 185, 129, 0.12)',
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
    margin: '3px',
  },
}))

const SoftSkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(245, 158, 11, 0.08)',
  border: '1px solid rgba(245, 158, 11, 0.2)',
  color: '#f59e0b',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
    margin: '3px',
  },
}))

const ValueChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(139, 92, 246, 0.08)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  color: '#8b5cf6',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
    margin: '3px',
  },
}))

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
}))

const TitleBox = styled(Typography)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 14,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  textTransform: 'uppercase',
  [theme.breakpoints.down('sm')]: {
    fontSize: 12,
  },
}))

export default function OffreDetail() {
  const { t, i18n } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  const user = useSelector((state) => state.userAuth.user)

  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isFavorite, setIsFavorite] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [applyModalOpen, setApplyModalOpen] = useState(false)

  useEffect(() => {
    if (id) {
      getJobDetails(id)
    }
  }, [id])

  const getJobDetails = async (jobId) => {
    setLoading(true)
    try {
      let result = await Api.besoinEntreprise.detailPost(jobId)

      const handling = handlingErrors(result)

      if (handling.status === 401) {
        setLoading(false)
        toast.error(t('successMessage.sessionExpired'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        return
      }

      if (handling.ok) {
        const res = handling.data
        setJob(res)
        setLoading(false)
      } else {
        setLoading(false)
        toast.error(t('successMessage.somethingWrong'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
      }
    } catch (error) {
      setLoading(false)
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        icon: ({ theme, type }) => (
          <FontAwesomeIcon
            icon={faCircleExclamation}
            fontSize={25}
            color={lightTheme.palette.error.main}
          />
        ),
      })
    }
  }

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite)
    // TODO: Implement API call to save/remove favorite
  }

  const handleShare = () => {
    setShareModalOpen(true)
  }

  const handleShareOption = (option) => {
    const jobTitle = job.poste?.titre || job.intitule || job.title
    const jobDescription = job.description || job.descriptionPoste
    const jobUrl = window.location.href

    if (option.url) {
      window.open(option.url, '_blank')
    } else if (option.action) {
      option.action()
    }
    setShareModalOpen(false)
  }

  const getShareOptions = () => {
    const jobTitle = job.poste?.titre || job.intitule || job.title
    const jobDescription = job.description || job.descriptionPoste
    const jobUrl = window.location.href

    return [
      {
        name: 'WhatsApp',
        url: `https://wa.me/?text=${encodeURIComponent(
          `${jobTitle}\n${jobDescription}\n${jobUrl}`
        )}`,
        icon: faWhatsapp,
        color: '#25D366',
      },
      {
        name: 'Facebook',
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          jobUrl
        )}&quote=${encodeURIComponent(jobTitle)}`,
        icon: faFacebook,
        color: '#1877F2',
      },
      {
        name: 'LinkedIn',
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          jobUrl
        )}&summary=${encodeURIComponent(jobDescription)}`,
        icon: faLinkedin,
        color: '#0077B5',
      },
      {
        name: 'Twitter',
        url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          `${jobTitle} ${jobUrl}`
        )}`,
        icon: faTwitter,
        color: '#1DA1F2',
      },
      {
        name: t('jobOffers.copyLink'),
        action: () => {
          navigator.clipboard.writeText(jobUrl)
          toast.success(t('jobOffers.linkCopied'), {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: true,
          })
        },
        icon: faShare,
        color: '#667eea',
      },
    ]
  }

  const handleApply = () => {
    setApplyModalOpen(true)
  }

  const handleCloseApplyModal = () => {
    setApplyModalOpen(false)
  }

  const handleOnlyCandidature = () => {
    toast.warning(t('jobOffers.onlyCandidat'), {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: true,
    })
  }

  const handleBack = () => {
    navigate('/offres-emploi')
  }


  const sizeChip = useMediaQuery(lightTheme.breakpoints.down('sm')) ? 'small' : 'medium'
  const isMObile = useMediaQuery(lightTheme.breakpoints.down('sm')) ? true : false

  if (loading) {
    return (
      <main>
        <Box
          width={'100%'}
          component={'section'}
          sx={{
            padding: { xs: '110px 0 130px 0', md: '100px 0 ', lg: '80px 0' },
          }}
        >
          <Container maxWidth={'lg'} sx={{ px: { xs: 2, md: 0 } }}>
            <Box sx={{ mb: 4 }}>
              <Skeleton variant="text" width={200} height={40} />
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rectangular" width="100%" height={400} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" width="100%" height={300} />
              </Grid>
            </Grid>
          </Container>
        </Box>
      </main>
    )
  }

  if (!job) {
    return (
      <main>
        <Box
          width={'100%'}
          component={'section'}
          sx={{
            padding: { xs: '110px 0 130px 0', md: '100px 0 ', lg: '80px 0' },
          }}
        >
          <Container maxWidth={'lg'} sx={{ px: { xs: 2, md: 0 } }}>
            <Typography variant="h4" align="center" sx={{ mb: 4 }}>
              {t('jobOffers.jobNotFound', 'Offre non trouvée')}
            </Typography>
            <Box textAlign="center">
              <Button
                variant="contained"
                onClick={handleBack}
                startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                {t('jobOffers.backToList', 'Retour à la liste')}
              </Button>
            </Box>
          </Container>
        </Box>
      </main>
    )
  }

  return (
    <main>
      <Box
        width={'100%'}
        component={'section'}
        sx={{
          padding: { xs: '40px 0 130px 0', md: '100px 0 ', lg: '80px 0' },
        }}
      >
        <Container maxWidth={'lg'} sx={{ px: { xs: 2, md: 0 } }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 4 }} separator="›">
            <MuiLink
              component={Link}
              to="/"
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              {t('button.home')}
            </MuiLink>
            <MuiLink component={Link} to="/offres-emploi" underline="hover">
              {t('jobOffers.title')}
            </MuiLink>
            <Typography color="text.primary">{job.poste?.titre}</Typography>
          </Breadcrumbs>

          {/* Back button */}
          <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('jobOffers.backToList')}
            </Button>
          </Box>

          <Grid container spacing={4}>
            {/* Main content */}
            <Grid item xs={12} md={8}>
              <StyledCard>
                <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                  {/* Job header */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h3"
                      component="h1"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', md: '2rem' },
                        lineHeight: 1.2,
                        color: '#1a1a1a',
                        mb: 2,
                      }}
                    >
                      {user &&
                        user.data.role !== ROLE_CANDIDAT &&
                        job.postuler && <LiveBadge label={' '} />}{' '}
                      {job.poste?.titre}
                    </Typography>

                    <Box
                      sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}
                    >
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          px: { xs: 2, sm: 3 },
                          py: { xs: 0.75, sm: 1 },
                          backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          borderRadius: '20px',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: '#667eea',
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                          }}
                        >
                          {job.entreprise?.nom ||
                            job.company ||
                            t('contact.company')}
                        </Typography>
                      </Box>

                      {job.nbrPoste && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: { xs: 2, sm: 3 },
                            py: { xs: 0.75, sm: 1 },
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            borderRadius: '20px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: '#10b981',
                              fontSize: { xs: '0.85rem', sm: '1rem' },
                            }}
                          >
                            {job.nbrPoste}{' '}
                            {job.nbrPoste > 1
                              ? t('filter.poste') + 's'
                              : t('filter.poste')}
                          </Typography>
                        </Box>
                      )}

                      {user &&
                        user.data.role === ROLE_CANDIDAT &&
                        job.candidateMatchingScore ? (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: { xs: 2, sm: 3 },
                            py: { xs: 0.75, sm: 1 },
                            backgroundColor:
                              job.candidateMatchingScore >= 80
                                ? 'rgba(16, 185, 129, 0.1)'
                                : job.candidateMatchingScore >= 60
                                  ? 'rgba(245, 158, 11, 0.1)'
                                  : 'rgba(239, 68, 68, 0.1)',
                            borderRadius: '20px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color:
                                job.candidateMatchingScore >= 80
                                  ? '#10b981'
                                  : job.candidateMatchingScore >= 60
                                    ? '#f59e0b'
                                    : '#ef4444',
                              fontSize: { xs: '0.85rem', sm: '0.875rem' },
                            }}
                          >
                            {Math.round(job.candidateMatchingScore)}% Match
                          </Typography>
                        </Box>
                      ) : <></>}
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3 }}>

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          width: { xs: '100%', sm: 'auto' },
                          justifyContent: { xs: 'flex-start', sm: 'flex-start' },
                          flexWrap: 'wrap',
                        }}
                      >
                        {(!user ||
                          (user && user.data.role === ROLE_CANDIDAT)) && (
                            <IconButton
                              onClick={toggleFavorite}
                              sx={{
                                width: '48px',
                                height: '48px',
                                minWidth: '48px',
                                color: isFavorite ? '#ef4444' : '#9ca3af',
                                backgroundColor: isFavorite
                                  ? 'rgba(239, 68, 68, 0.1)'
                                  : 'rgba(156, 163, 175, 0.1)',
                                '&:hover': {
                                  backgroundColor: isFavorite
                                    ? 'rgba(239, 68, 68, 0.2)'
                                    : 'rgba(156, 163, 175, 0.2)',
                                },
                              }}
                            >
                              <FontAwesomeIcon icon={faHeart} />
                            </IconButton>
                          )}
                        <IconButton
                          onClick={handleShare}
                          sx={{
                            width: '48px',
                            height: '48px',
                            minWidth: '48px',
                            color: '#9ca3af',
                            backgroundColor: 'rgba(156, 163, 175, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(156, 163, 175, 0.2)',
                            },
                          }}
                        >
                          <FontAwesomeIcon icon={faShare} />
                        </IconButton>
                      </Box>
                      {(!user ||
                        (user && user.data.role === ROLE_CANDIDAT)) && (
                          <Button
                            variant="contained"
                            size="large"
                            fullWidth={isMObile}
                            onClick={
                              user && user.data.role === ROLE_CANDIDAT
                                ? handleApply
                                : () => {
                                  // Store the current path as referer before navigating
                                  localStorage.setItem(
                                    'loginReferer',
                                    window.location.pathname
                                  )
                                  navigate('/auth/se-connecter')
                                }
                            }
                            sx={{
                              background:
                                'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                            }}
                          >
                            {t('jobOffers.apply')}
                          </Button>
                        )}
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 4 }} />

                  {/* Job description */}
                  <Box sx={{ mb: 4 }}>
                    <TitleContainer>
                      <TitleBox>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FontAwesomeIcon
                            icon={faBriefcase}
                            color="#667eea"
                            fontSize={16}
                          />
                          {t('box.jobDescription')}
                        </Box>
                      </TitleBox>
                    </TitleContainer>
                    <Typography variant="body2" sx={{ color: '#0c4a6e' }}>
                      <HTMLContent
                        dangerouslySetInnerHTML={{
                          __html:
                            job.descriptifPoste ||
                            job.description ||
                            job.descriptionPoste ||
                            '',
                        }}
                      />
                    </Typography>
                  </Box>

                  {/* Requirements */}
                  {(job.competences || job.skills) && (
                    <Box sx={{ mb: 4 }}>
                      <TitleContainer>
                        <TitleBox>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FontAwesomeIcon
                              icon={faGraduationCap}
                              color="#667eea"
                              fontSize={16}
                            />
                            {t('filter.competences')}
                          </Box>
                        </TitleBox>
                      </TitleContainer>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {(job.competences || job.skills || []).map(
                          (skill, index) => (
                            <SkillChip
                              key={index}
                              label={skill.nom || skill}
                              size={sizeChip}
                            />
                          )
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Languages */}
                  {job.langues && job.langues.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <TitleContainer>
                        <TitleBox>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FontAwesomeIcon
                              icon={faLanguage}
                              color="#667eea"
                              fontSize={16}
                            />
                            {t('filter.languages')}
                          </Box>
                        </TitleBox>
                      </TitleContainer>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {job.langues.map((lang, index) => (
                          <LanguageChip
                            key={index}
                            label={lang}
                            size={sizeChip}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Additional Skills */}
                  {job.competencesAnnexes &&
                    job.competencesAnnexes.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <TitleContainer>
                          <TitleBox>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FontAwesomeIcon
                                icon={faChartLine}
                                color="#f59e0b"
                                fontSize={16}
                              />
                              {t('filter.competencesAnnexes')}
                            </Box>
                          </TitleBox>
                        </TitleContainer>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {job.competencesAnnexes.map((skill, index) => (
                            <SkillChip
                              key={index}
                              label={skill}
                              size={sizeChip}
                              sx={{
                                backgroundColor: 'rgba(245, 158, 11, 0.08)',
                                border: '1px solid rgba(245, 158, 11, 0.2)',
                                color: '#f59e0b',
                                '&:hover': {
                                  backgroundColor: 'rgba(245, 158, 11, 0.12)',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                  {/* Soft Skills */}
                  {job.softskils && job.softskils.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <TitleContainer>
                        <TitleBox>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FontAwesomeIcon
                              icon={faBrain}
                              color="#667eea"
                              fontSize={16}
                            />
                            {t('filter.softskils')}
                          </Box>
                        </TitleBox>
                      </TitleContainer>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {job.softskils.map((skill, index) => (
                          <SoftSkillChip
                            key={index}
                            label={skill}
                            size={sizeChip}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Values */}
                  {job.valeurs && job.valeurs.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <TitleContainer>
                        <TitleBox>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FontAwesomeIcon
                              icon={faHandshake}
                              color="#667eea"
                              fontSize={16}
                            />
                            {t('filter.values')}
                          </Box>
                        </TitleBox>
                      </TitleContainer>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {job.valeurs.map((value, index) => (
                          <ValueChip key={index} label={value} size={sizeChip} />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Benefits */}
                  {job.avantagesEntreprise &&
                    job.avantagesEntreprise.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <TitleContainer>
                          <TitleBox>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FontAwesomeIcon
                                icon={faHeart}
                                color="#667eea"
                                fontSize={16}
                              />
                              {t('filter.benefits')}
                            </Box>
                          </TitleBox>
                        </TitleContainer>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {job.avantagesEntreprise.map((benefit, index) => (
                            <Box
                              key={index}
                              sx={{
                                px: 2,
                                py: 1,
                                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                border: '1px solid rgba(16, 185, 129, 0.2)',
                                borderRadius: '8px',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#245343',
                                  fontWeight: 500,
                                }}
                              >
                                {benefit}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  {job.avantages && (
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: '#1a1a1a',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faHeart}
                          style={{
                            color: '#10b981',
                            marginRight: '8px',
                            fontSize: '20px',
                          }}
                        />
                        {t('filter.benefits')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.85rem' }}
                        >
                          {job.avantages}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              {/* Job details card */}
              <StyledCard>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      color: '#1a1a1a',
                    }}
                  >
                    {t('box.title.detailJob')}
                  </Typography>

                  {/* Location */}
                  <InfoBox>
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      style={{
                        color: '#667eea',
                        fontSize: '18px',
                        marginRight: '12px',
                        minWidth: '24px',
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {job.lieu || job.ville || job.location || 'Remote'}
                    </Typography>
                  </InfoBox>

                  {/* Contract type */}
                  <InfoBox>
                    <FontAwesomeIcon
                      icon={faBuilding}
                      style={{
                        color: '#10b981',
                        fontSize: '18px',
                        marginRight: '12px',
                        minWidth: '24px',
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {job.typeContrat || job.type || '---'}
                    </Typography>
                  </InfoBox>

                  {/* Salary */}
                  {job.salaireSur && (
                    <InfoBox>
                      <FontAwesomeIcon
                        icon={faEuroSign}
                        style={{
                          color: '#fbbf24',
                          fontSize: '18px',
                          marginRight: '12px',
                          minWidth: '24px',
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {job.devise?.toUpperCase() || 'CHF'} {job.salaire}/
                        {t('box.mounth')}
                      </Typography>
                    </InfoBox>
                  )}

                  {/* Posted date */}
                  <InfoBox>
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      style={{
                        color: '#8b5cf6',
                        fontSize: '18px',
                        marginRight: '12px',
                        minWidth: '24px',
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {t('jobOffers.postedOn')}{' '}
                      {moment(job.date)
                        .tz('Europe/Paris')
                        .format('DD/MM/YYYY')}
                    </Typography>
                  </InfoBox>

                  {/* Work hours */}
                  {job.horaires && (
                    <InfoBox>
                      <FontAwesomeIcon
                        icon={faClock}
                        style={{
                          color: '#f59e0b',
                          fontSize: '18px',
                          marginRight: '12px',
                          minWidth: '24px',
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <strong>{t('jobOffers.hours')}:</strong> {job.horaires}
                      </Typography>
                    </InfoBox>
                  )}

                  {/* Remote work */}
                  {job.teletravailPropose !== undefined && (
                    <InfoBox>
                      <FontAwesomeIcon
                        icon={faUserTie}
                        style={{
                          color: '#06b6d4',
                          fontSize: '18px',
                          marginRight: '12px',
                          minWidth: '24px',
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <strong>{t('jobOffers.remoteWork')}:</strong>{' '}
                        {`${job.teletravailPropose} ${t('box.days')}`}
                      </Typography>
                    </InfoBox>
                  )}

                  {/* Work type */}
                  {job.typeTempsTravail && (
                    <InfoBox>
                      <FontAwesomeIcon
                        icon={faClock}
                        style={{
                          color: '#10b981',
                          fontSize: '18px',
                          marginRight: '12px',
                          minWidth: '24px',
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <strong>{t('box.timeWorkType')}:</strong>{' '}
                        {job.typeTempsTravail === 'plein'
                          ? t('box.timeWorkType1')
                          : t('box.timeWorkType2')}
                      </Typography>
                    </InfoBox>
                  )}

                  {/* Direct report */}
                  {job.reportDirect && (
                    <InfoBox>
                      <FontAwesomeIcon
                        icon={faUserCheck}
                        style={{
                          color: '#8b5cf6',
                          fontSize: '18px',
                          marginRight: '12px',
                          minWidth: '24px',
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <strong>{t('box.jobReport')}:</strong>{' '}
                        {job.reportDirect}
                      </Typography>
                    </InfoBox>
                  )}

                  {/* Travel requirements */}
                  {(job.deplacement !== undefined ||
                    job.permisRequis !== undefined ||
                    job.vehiculeRequis !== undefined) && (
                      <InfoBox>
                        <FontAwesomeIcon
                          icon={faRoad}
                          style={{
                            color: '#f59e0b',
                            fontSize: '18px',
                            marginRight: '12px',
                            minWidth: '24px',
                          }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          <strong>{t('jobOffers.requirements')}:</strong>
                          <Box component="span" sx={{ ml: 1 }}>
                            {job.deplacement && (
                              <Box component="span" sx={{ mr: 1 }}>
                                Déplacements requis
                              </Box>
                            )}
                            {job.permisRequis && (
                              <Box component="span" sx={{ mr: 1 }}>
                                Permis requis
                              </Box>
                            )}
                            {job.vehiculeRequis && (
                              <Box component="span" sx={{ mr: 1 }}>
                                Véhicule requis
                              </Box>
                            )}
                            {!job.deplacement &&
                              !job.permisRequis &&
                              !job.vehiculeRequis &&
                              t('jobOffers.noRequirements')}
                          </Box>
                        </Typography>
                      </InfoBox>
                    )}

                  {/* Job type */}
                  {job.type && (
                    <InfoBox>
                      <FontAwesomeIcon
                        icon={faBriefcase}
                        style={{
                          color: '#667eea',
                          fontSize: '18px',
                          marginRight: '12px',
                          minWidth: '24px',
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <strong>{t('box.jobPositionType')}:</strong> {job.type}
                      </Typography>
                    </InfoBox>
                  )}

                  {/* Country */}
                  {job.pays && (
                    <InfoBox>
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        style={{
                          color: '#ef4444',
                          fontSize: '18px',
                          marginRight: '12px',
                          minWidth: '24px',
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <strong>{t('contact.country')}:</strong> {job.pays}
                      </Typography>
                    </InfoBox>
                  )}
                </CardContent>
              </StyledCard>

              {/* Company info card */}
              {job.entreprise && (
                <StyledCard>
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 3,
                        color: '#1a1a1a',
                      }}
                    >
                      {t('jobOffers.companyInfo', 'Informations entreprise')}
                    </Typography>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      {job.entreprise.logo ? (
                        <img
                          src={job.entreprise.logo}
                          alt={job.entreprise.nom}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '80px',
                            height: '80px',
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faBuilding}
                            style={{
                              color: '#667eea',
                              fontSize: '32px',
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        textAlign: 'center',
                        mb: 1,
                        color: '#1a1a1a',
                      }}
                    >
                      {job.entreprise.nom}
                    </Typography>
                    {job.entreprise.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.5,
                          color: '#6b7280',
                          textAlign: 'center',
                        }}
                      >
                        {job.entreprise.description}
                      </Typography>
                    )}
                  </CardContent>
                </StyledCard>
              )}
            </Grid>
          </Grid>
        </Container>

        {/* Share Modal */}
        <Modal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          aria-labelledby="share-modal-title"
          aria-describedby="share-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: { xs: '18px', sm: '24px' },
              width: { xs: 'min(95vw, 360px)', sm: '400px' },
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            }}
          >
            <Typography
              id="share-modal-title"
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                color: '#1a1a1a',
              }}
            >
              {t('jobOffers.shareOffer')}
            </Typography>

            <List sx={{ py: 1 }}>
              {getShareOptions().map((option, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleShareOption(option)}
                  sx={{
                    borderRadius: '8px',
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.04)',
                    },
                  }}
                >
                  <ListItemIcon>
                    <FontAwesomeIcon
                      icon={option.icon}
                      style={{
                        color: option.color,
                        fontSize: '20px',
                        width: '20px',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={option.name}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: 500,
                        color: '#374151',
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => setShareModalOpen(false)}
                sx={{
                  borderColor: '#e5e7eb',
                  color: '#6b7280',
                  '&:hover': {
                    backgroundColor: '#f9fafb',
                  },
                }}
              >
                {t('button.cancel', 'Annuler')}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Application Modal */}
        <ApplicationModal
          open={applyModalOpen}
          onClose={handleCloseApplyModal}
          job={job}
        />
      </Box>
    </main>
  )
}
